import { useDispatch } from "react-redux";

import PaletteIcon from "@material-ui/icons/Palette";
import { IS_AWS_ENV } from "constants/app.constants";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import {
  RIG_FRAC_PLASMA_PALETTE,
  convertGeneratedPaletteToChartRequestShape
} from "entities/charts/eva/requests/rigFracColorPalette";
import { updateChart } from "store/features/charts/chartSlice";
import { ChartRequestType, RigFracActivityChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { BaseMenu, ToolbarButton } from "components/base";
import ColorPaletteList from "components/filter/focus/ColorPaletteList";
import { IColorPalette } from "components/user-settings/models";

/**
 * Palette control for the Rig/Frac charts (EVA-76). The backend colors the bars
 * from `request.colorPalette`; this writes the picked palette there (falling
 * back to the Plasma default for display) which re-triggers the controller's POST.
 * IColorPalette ↔ ColorPaletteSetting overlap on the fields the backend reads,
 * so cast at the request seam.
 *
 * Composes the same picker (ColorPaletteList) the shared ColorPaletteSelector
 * renders internally, but behind an icon-only toolbar trigger instead of the
 * shared selector's fixed name+strip preview. `includeGeneratedPalettes`
 * matches the map icon palette picker (RigFracColorActions) so both offer the
 * same union of options: every org palette plus every generated colormap
 * palette, Plasma included — never fewer than either list has on its own.
 */
// buildColormapPalette's own id convention (utils/color/buildColormapPalette.ts).
// Every generated entry the union-mode picker can hand back — Plasma
// included — carries this prefix.
const GENERATED_PALETTE_ID_PREFIX = "generated-";

function RigFracColorPaletteSelector() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request } = useChartEntities(id);

  const selections = request as unknown as Maybe<
    Partial<RigFracActivityChartRequestType>
  >;
  // Plasma is the rig/frac default, so it shows as selected until the user
  // picks another (matches the controller's fallback).
  const selectedPalette =
    (selections?.colorPalette as unknown as IColorPalette) ??
    (RIG_FRAC_PLASMA_PALETTE as unknown as IColorPalette);

  const handlePaletteChange = (palette: IColorPalette, isPick: boolean) => {
    // A generated pick (hex colours, no colorScale) must be converted to the
    // rgb()+colorScale shape before it reaches the request — chart-svc's
    // ParseRgbColor only reads literal rgb(...) strings. An org-saved palette
    // is already in that shape (state.userSetting.colorPalettes seeds/uploads
    // are rgb already), so it passes through untouched.
    const chartPalette = palette.id?.startsWith(GENERATED_PALETTE_ID_PREFIX)
      ? (convertGeneratedPaletteToChartRequestShape(palette) as unknown as IColorPalette)
      : palette;
    // On AWS the default follows the focus palette, so a pick is marked to keep
    // an explicit Plasma pick painting Plasma. Reversing keeps the current mark.
    const userPicked = isPick || selections?.colorPalette?.userPicked === true;

    dispatch(
      updateChart({
        id,
        request: {
          ...DEFAULT_RIG_FRAC_ACTIVITY_REQUEST,
          ...selections,
          colorPalette: IS_AWS_ENV ? { ...chartPalette, userPicked } : chartPalette
        } as unknown as ChartRequestType
      })
    );
  };

  return (
    <div className="charts-selector flex" data-testid="rig-frac-color-palette">
      <BaseMenu
        trigger={
          <ToolbarButton
            data-testid="rig-frac-color-palette-trigger"
            aria-label="Select Colour Palette"
            active={false}
            icon={<PaletteIcon fontSize="large" />}
            tooltipText="Select Colour Palette"
          />
        }>
        {({ closeMenu }) => (
          <ColorPaletteList
            selected={selectedPalette}
            onSelect={(palette: IColorPalette) => {
              handlePaletteChange(palette, true);
              closeMenu();
            }}
            onReverse={(palette: IColorPalette) => handlePaletteChange(palette, false)}
            includeGeneratedPalettes
          />
        )}
      </BaseMenu>
    </div>
  );
}

export default RigFracColorPaletteSelector;
